import type { ConnectifSend } from '~~/modules/integrations/connectif/entities'
import { useUserStore } from '~~/modules/auth/stores/userStore'

const connectifService = {
  // State management
  _state: 'DISABLED' as 'DISABLED' | 'LOADING' | 'ENABLED',
  _processingQueue: false,
  _userStoreReady: false,
  _connectifReady: false,

  // Queue with auto-processing trigger
  queue: new Proxy([] as ConnectifSend[], {
    set: (target: ConnectifSend[], property: string | symbol, value) => {
      ;(target as unknown as Record<string | symbol, unknown>)[property] = value
      if (property === 'length' && !connectifService._processingQueue) {
        connectifService._processQueue()
      }
      return true
    },
  }),

  // Public API
  send: (params: ConnectifSend) => {
    connectifService.queue.push(params)
  },

  // Initialization Methods
  _initializeUserStore() {
    if (import.meta.client && !connectifService._userStoreReady) {
      const userStore = useUserStore()

      const unsubscribe = watch(
        () => userStore.state.value.logged,
        () => {
          checkUserStore()
        },
      )

      const checkUserStore = () => {
        if (userStore?.getLogged.value !== undefined) {
          connectifService._userStoreReady = true
          unsubscribe()
          connectifService._checkReadiness()
        }
      }

      checkUserStore()
    }
  },

  _initializeConnectif() {
    const onConnectifInitialized = () => {
      connectifService._connectifReady = true
      connectifService._checkReadiness()
      connectifService._removeConnectifListeners(
        onConnectifInitialized,
        removeListener,
      )
    }

    const removeListener = () => {
      connectifService._removeConnectifListeners(
        onConnectifInitialized,
        removeListener,
      )
    }

    document.addEventListener(
      'connectif.managed.initialized',
      onConnectifInitialized,
    )
    window.addEventListener('pagehide', removeListener, { once: true })
  },

  _removeConnectifListeners(
    onInitialized: () => void,
    removeListener: () => void,
  ) {
    document.removeEventListener('connectif.managed.initialized', onInitialized)
    window.removeEventListener('pagehide', removeListener)
  },

  // Readiness Check
  _checkReadiness() {
    if (connectifService._userStoreReady && connectifService._connectifReady) {
      connectifService._state = 'ENABLED'
      connectifService._processQueue()
    }
  },

  // Queue Processing
  _processQueue() {
    if (connectifService._state === 'ENABLED') {
      connectifService._sendQueuedItems()
    } else if (connectifService._state === 'DISABLED') {
      connectifService._startInitialization()
    }
  },

  _startInitialization() {
    connectifService._state = 'LOADING'
    connectifService._initializeUserStore()
    connectifService._initializeConnectif()
  },

  _sendQueuedItems() {
    connectifService._processingQueue = true

    while (this.queue.length) {
      const item = this.queue.shift()
      if (item) {
        const enhancedItem = connectifService._enhanceWithUserInfo(item)

        window.connectif.managed.sendEvents(
          enhancedItem.events,
          enhancedItem.options,
        )
      }
    }

    connectifService._processingQueue = false
  },

  // User Info Enhancement
  _enhanceWithUserInfo(item: ConnectifSend): ConnectifSend {
    if (import.meta.client && connectifService._userStoreReady) {
      const userStore = useUserStore()
      const user = userStore.getUserdata.value

      if (user && user.logged && !item.options?.entityInfo) {
        const enhanced = {
          ...item,
          options: {
            ...item.options,
            entityInfo: {
              primaryKey: user.mail as string,
            },
          },
        }
        return enhanced
      }
    }
    return item
  },
}

export default connectifService
